/**
 * BarChartPreview Component
 * Handles rendering for vertical, horizontal, grouped, and stacked bar charts
 */

import { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridRows, GridColumns } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { getColors, resolveColorWithOverrides, resolveColorByMapping } from '../../charts/shared/palettes';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import { HatchPatternDefs } from './HatchPatterns';
import {
  resolveXAxisLabels,
  resolveYAxisLabels,
  makeTickComponent,
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  getXAxisBaselineNudge,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  niceDomain,
  computeLegendRows,
  LEGEND_SPACING,
  legendTopTranslateY,
  legendTopSpace,
  formatCompactNumber,
  getCompactTickFormatter,
  computeBarWidth,
} from '../../utils/chartLayout';
import ChartTitle from './ChartTitle';
import ChartLegend from './ChartLegend';
import { getAccessibleTextColor } from '../../utils/accessibility';
import { chartFonts } from '../../utils/chartFonts';

// ─── Sample data generators (unchanged from original) ─────────────────────────

function generateBarData(n) {
  const data = [];
  const values = [85.2, 78.5, 92.1, 88.7, 81.3, 76.9, 94.5, 83.2, 79.8, 91.4];
  for (let i = 0; i < n; i++) {
    data.push({ name: `Cat ${i + 1}`, value: values[i % values.length] });
  }
  return data;
}

function generateGroupedData(numCategories, numSeries) {
  const data = [];
  const baseValues = [78.2, 85.3, 88.7, 90.5, 82.1, 86.4, 89.2, 91.8, 84.6, 87.9];
  for (let i = 0; i < numCategories; i++) {
    const item = { name: `Group ${i + 1}` };
    for (let j = 0; j < numSeries; j++) {
      item[`series${j + 1}`] = baseValues[(i + j * 3) % baseValues.length];
    }
    data.push(item);
  }
  return data;
}

function generateStackedData(numCategories, numStacks) {
  const data = [];
  const baseValues = [45, 35, 20, 50, 30, 25, 40, 28, 32, 38];
  for (let i = 0; i < numCategories; i++) {
    const item = { name: `Cat ${i + 1}` };
    for (let j = 0; j < numStacks; j++) {
      item[`stack${j + 1}`] = baseValues[(i + j * 2) % baseValues.length];
    }
    data.push(item);
  }
  return data;
}

// ─── SVG path helpers ─────────────────────────────────────────────────────────

// Rectangle with top corners rounded (vertical bars)
function topRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h, w / 2);
  if (radius <= 0 || h <= 0) return `M${x},${y + h} L${x},${y} L${x + w},${y} L${x + w},${y + h} Z`;
  return [
    `M${x},${y + h}`,
    `L${x},${y + radius}`,
    `Q${x},${y} ${x + radius},${y}`,
    `L${x + w - radius},${y}`,
    `Q${x + w},${y} ${x + w},${y + radius}`,
    `L${x + w},${y + h}`,
    `Z`,
  ].join(' ');
}

// Rectangle with right corners rounded (horizontal bars)
function rightRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w);
  if (radius <= 0 || w <= 0) return `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
  return [
    `M${x},${y}`,
    `L${x + w - radius},${y}`,
    `Q${x + w},${y} ${x + w},${y + radius}`,
    `L${x + w},${y + h - radius}`,
    `Q${x + w},${y + h} ${x + w - radius},${y + h}`,
    `L${x},${y + h}`,
    `Z`,
  ].join(' ');
}

// ─── Shared axis label renderer ───────────────────────────────────────────────

function AxisLabels({ xlabel, ylabel, innerWidth, innerHeight, yAxisTitleOffset, xAxisTitleY, theme }) {
  const labelSize = theme.labelSize ?? 15;
  const fonts = chartFonts(theme);
  return (
    <>
      <text
        x={-innerHeight / 2}
        y={-yAxisTitleOffset}
        transform="rotate(-90)"
        textAnchor="middle"
        fontSize={labelSize}
        fontFamily={fonts.label}
        fill={theme.labelColor}
      >
        {ylabel || ''}
      </text>
      <text
        x={innerWidth / 2}
        y={xAxisTitleY}
        textAnchor="middle"
        fontSize={labelSize}
        fontFamily={fonts.label}
        fill={theme.labelColor}
      >
        {xlabel || ''}
      </text>
    </>
  );
}

// ─── Reference line (horizontal) ─────────────────────────────────────────────

function HRefLine({ yScale, value, label, innerWidth, color, fontFamily }) {
  const y = yScale(value);
  return (
    <g>
      <line
        x1={0} y1={y} x2={innerWidth} y2={y}
        stroke={color}
        strokeDasharray="5 5"
        strokeWidth={2}
      />
      <text x={innerWidth} y={y - 4} textAnchor="end" fill={color} fontSize={13} fontFamily={fontFamily}>
        {label || 'Reference'}
      </text>
    </g>
  );
}

// ─── Reference line (vertical) ────────────────────────────────────────────────

function VRefLine({ xScale, value, label, innerHeight, color, fontFamily }) {
  const x = xScale(value);
  return (
    <g>
      <line
        x1={x} y1={0} x2={x} y2={innerHeight}
        stroke={color}
        strokeDasharray="5 5"
        strokeWidth={2}
      />
      <text x={x + 4} y={8} textAnchor="start" fill={color} fontSize={13} fontFamily={fontFamily}>
        {label || 'Reference'}
      </text>
    </g>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function BarChartPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: rawData, mapping } = useData();

  // Generate dynamic data based on chart type and config
  const data = useMemo(() => {
    let processedData;

    if (isLoaded && rawData.length > 0 && Object.keys(mapping).length > 0) {
      processedData = transformData(rawData, mapping, chartType.id);
      if (!processedData || processedData.length === 0) {
        processedData = generateBarData(config.numCategories || 5);
      }
    } else if (chartType.id === 'vertical-bar' || chartType.id === 'horizontal-bar') {
      processedData = generateBarData(config.numCategories || 5);
    } else if (chartType.id === 'grouped-bar' || chartType.id === 'horizontal-grouped-bar') {
      processedData = generateGroupedData(config.numCategories || 4, config.numSeries || 3);
    } else if (chartType.id === 'stacked-bar' || chartType.id === 'horizontal-stacked-bar') {
      processedData = generateStackedData(config.numCategories || 4, config.numStacks || 3);
    } else {
      processedData = [...chartType.sampleData];
    }

    const getRowSortValue = (row) => {
      if (typeof row?.value === 'number' && Number.isFinite(row.value)) {
        return row.value;
      }
      const numericValues = Object.keys(row || {})
        .filter((key) => key !== 'name' && typeof row[key] === 'number' && Number.isFinite(row[key]))
        .map((key) => row[key]);
      if (numericValues.length === 0) return 0;
      const sum = numericValues.reduce((acc, current) => acc + current, 0);
      return sum / numericValues.length;
    };

    if (config.barOrdering === 'ascending') {
      processedData.sort((a, b) => getRowSortValue(a) - getRowSortValue(b));
    } else if (config.barOrdering === 'descending') {
      processedData.sort((a, b) => getRowSortValue(b) - getRowSortValue(a));
    }

    return processedData;
  }, [chartType.id, chartType.sampleData, config.numCategories, config.numSeries, config.numStacks, config.barOrdering, isLoaded, rawData, mapping]);

  // Colors
  const colors = useMemo(() => getColors(config, data.length, theme), [config, data.length, theme]);

  const tickSize = theme.tickSize ?? 13;
  const labelSize = theme.labelSize ?? 15;
  const valueLabelSize = theme.valueLabelSize ?? 12;
  const fonts = chartFonts(theme);
  const spacingSpec = getAxisSpacingSpec(theme);
  const { xTickGap, yTickGap, yTickBaselineNudge, xTickLength, yTickLength } = spacingSpec;
  const xTickBaselineNudgeNumeric = getXAxisBaselineNudge({ tickType: 'numeric', spacingSpec });
  const axisDebugEnabled = isAxisDebugEnabled();
  const axisTickProps = (fontFamily = fonts.number) => ({ fill: theme.tickColor, fontSize: tickSize, fontFamily });
  const referenceLineColor = theme.referenceLineColor || '#C44B3D';
  const baseLabelBg = theme.plotBackground || theme.background || '#FFFFFF';
  const getLabelColor = (background) => getAccessibleTextColor({
    background,
    preferred: theme.foreground || '#111111',
    minRatio: 4.5,
  });
  const shouldTruncateXAxisLabels = !!config.truncateXAxisLabels;

  // ─── Tooltip setup ─────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  // ─── vertical-bar ──────────────────────────────────────────────────────────
  if (chartType.id === 'vertical-bar') {
    const categories = data.map(d => d.name);
    const values = data.map(d => d.value).filter(v => v !== undefined);
    const [yMin, yMax] = niceDomain(values, {
      includeZero: true,
      minOverride: config.yMin,
      maxOverride: config.yMax,
    });

    const colorByResult = resolveColorByMapping(data, config, theme);
    let barFillFn;
    let vLegendItems = [];
    if (colorByResult) {
      barFillFn = colorByResult.fillFn;
      vLegendItems = colorByResult.legendItems;
    } else {
      barFillFn = (d, i) => resolveColorWithOverrides(colors[i % colors.length], d.name, config.colorOverrides);
    }
    const showVLegend = colorByResult ? true : !!config.showLegend;

    const titleFontSz = computeTitleFontSize(config.title, width);

    const xAxis = resolveXAxisLabels({
      labels: categories,
      chartWidth: width,
      chartHeight: height,
      baseFontSize: tickSize,
      fontFamily: theme.fontBody,
      rotationOverride: config.labelRotation,
      truncateLabels: shouldTruncateXAxisLabels,
    });
    const yTicks = scaleLinear({ domain: [yMin, yMax], range: [100, 0] }).ticks();
    const vLegendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
    const vEstimatedLegendRows = showVLegend && vLegendItems.length > 0
      ? computeLegendRows(vLegendItems, Math.max(120, width - 160), theme.legendSize ?? 12, vLegendMarkerScale).rowCount
      : 0;
    const vTopLegendSpace = showVLegend && vLegendItems.length > 0 ? legendTopSpace(vEstimatedLegendRows) : 0;
    const layout = computeChartLayout({
      width,
      height,
      title: config.title,
      titleFontSize: titleFontSz || undefined,
      xLabels: categories,
      yTickValues: yTicks,
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      hasXLabel: !!config.xlabel,
      hasYLabel: !!config.ylabel,
      orientation: 'vertical',
      baseRight: 80,
      baseBottom: 70,
      legend: vLegendItems.length > 0 ? {
        show: showVLegend,
        position: 'top',
        topSpace: vTopLegendSpace,
        rightSpace: 0,
        bottomSpace: 0,
      } : undefined,
      xAxisResolved: xAxis,
    });
    const { margin, innerWidth, innerHeight } = layout;
    const yLanes = computeAxisLanes({
      axisSide: 'left',
      labels: yTicks.map((v) => String(v)),
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
      telemetryMeta: { chartId: 'vertical-bar', viewportWidth: width, viewportHeight: height },
    });
    const xLanes = computeAxisLanes({
      axisSide: 'bottom',
      labels: categories.map((c) => xAxis.tickFormat(c)),
      rotation: xAxis.rotation || 0,
      tickFontSize: xAxis.fontSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: xTickLength,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
      telemetryMeta: { chartId: 'vertical-bar', viewportWidth: width, viewportHeight: height },
    });
    if (axisDebugEnabled) {
      reportAxisLaneClamp(yLanes);
      reportAxisLaneClamp(xLanes);
    }
    const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
    const xScale = scaleBand({ domain: categories, range: [0, innerWidth], padding: 0.35 });
    const yScale = scaleLinear({ domain: [yMin, yMax], range: [innerHeight, 0] });

    const bw = computeBarWidth(xScale.bandwidth(), innerWidth, categories.length, { maxFraction: 0.065 });
    const bOff = (xScale.bandwidth() - bw) / 2;
    const effectiveRadius = Math.min(theme.barRadius, bw * 0.15);

    const rv = config.showReferenceLine && config.referenceValue ? parseFloat(config.referenceValue) : null;

    return (
      <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
        <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
          <Group left={margin.left} top={margin.top}>
            {showVLegend && vLegendItems.length > 0 && (
              <ChartLegend
                items={vLegendItems}
                innerWidth={innerWidth}
                theme={theme}
                position="top"
                transform={`translate(0, ${legendTopTranslateY(vEstimatedLegendRows, width, height)})`}
              />
            )}
            {config.showGrid && (
              <GridRows
                scale={yScale}
                width={innerWidth}
                strokeDasharray="3 3"
                stroke={theme.gridColor}
                strokeOpacity={theme.gridAlpha}
              />
            )}

            {data.map((d, i) => {
              const x = (xScale(d.name) ?? 0) + bOff;
              const y = yScale(d.value);
              const barH = innerHeight - y;
              if (barH <= 0) return null;
              return (
                <g key={d.name} onMouseMove={(e) => showTip(e, formatCompactNumber(d.value, config.valueDecimals || 1))} style={{ cursor: 'pointer' }}>
                  <path d={topRoundedRect(x, y, bw, barH, effectiveRadius)} fill={barFillFn(d, i)} />
                  {config.showValues && (
                    <text x={x + bw / 2} y={y - 5} textAnchor="middle" fontSize={valueLabelSize} fontWeight={500} fontFamily={fonts.number} fill={getLabelColor(baseLabelBg)}>
                      {formatCompactNumber(d.value, config.valueDecimals || 1)}
                    </text>
                  )}
                </g>
              );
            })}

            {rv !== null && (
              <HRefLine yScale={yScale} value={rv} label={config.referenceLabel} innerWidth={innerWidth} color={referenceLineColor} fontFamily={fonts.label} />
            )}

            <AxisLeft
              scale={yScale}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={yTickLength}
              tickFormat={getCompactTickFormatter(yScale.domain()[1])}
              tickLabelProps={() => ({ ...axisTickProps(), textAnchor: 'end', dx: -yTickGap, dy: yTickBaselineNudge })}
            />
            <AxisBottom
              scale={xScale}
              top={innerHeight}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={xTickLength}
              tickValues={xAxis.tickValues}
              tickFormat={xAxis.tickFormat}
              tickComponent={makeTickComponent(xAxis.rotation, { fontSize: xAxis.fontSize, fill: theme.tickColor, fontFamily: fonts.label })}
              tickLabelProps={() => ({ opacity: 0 })}
            />

            <AxisLabels
              xlabel={config.xlabel}
              ylabel={config.ylabel}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              yAxisTitleOffset={yLanes.titleOffsetPx}
              xAxisTitleY={xAxisTitleY}
              theme={theme}
            />
            {axisDebugEnabled && (
              <AxisLaneDebugOverlay
                lanes={[
                  {
                    axisSide: 'left',
                    tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                    titleOffsetPx: yLanes.titleOffsetPx,
                    clamped: yLanes.clamped,
                    spineX: 0,
                    spineY1: 0,
                    spineY2: innerHeight,
                  },
                  {
                    axisSide: 'bottom',
                    tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                    titleOffsetPx: xLanes.titleOffsetPx,
                    clamped: xLanes.clamped,
                    spineY: innerHeight,
                    spineX1: 0,
                    spineX2: innerWidth,
                  },
                ]}
              />
            )}
          </Group>
        </svg>
        <Tip />
      </div>
    );
  }

  // ─── horizontal-bar ────────────────────────────────────────────────────────
  if (chartType.id === 'horizontal-bar') {
    const categories = data.map(d => d.name);

    const hColorByResult = resolveColorByMapping(data, config, theme);
    let hBarFillFn;
    let hLegendItems = [];
    if (hColorByResult) {
      hBarFillFn = hColorByResult.fillFn;
      hLegendItems = hColorByResult.legendItems;
    } else {
      hBarFillFn = (d, i) => resolveColorWithOverrides(colors[i % colors.length], d.name, config.colorOverrides);
    }
    const showHLegend = hColorByResult ? true : !!config.showLegend;

    const hTitleFontSz = computeTitleFontSize(config.title, width);
    const hLegendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
    const hEstimatedLegendRows = showHLegend && hLegendItems.length > 0
      ? computeLegendRows(hLegendItems, Math.max(120, width - 160), theme.legendSize ?? 12, hLegendMarkerScale).rowCount
      : 0;
    const hTopLegendSpace = showHLegend && hLegendItems.length > 0 ? legendTopSpace(hEstimatedLegendRows) : 0;
    const layout = computeChartLayout({
      width,
      height,
      title: config.title,
      titleFontSize: hTitleFontSz || undefined,
      xLabels: categories,
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      hasYLabel: !!config.ylabel,
      orientation: 'horizontal',
      baseRight: 60,
      baseBottom: 70,
      horizontalLeftMax: 180,
      legend: hLegendItems.length > 0 ? {
        show: showHLegend,
        position: 'top',
        topSpace: hTopLegendSpace,
        rightSpace: 0,
        bottomSpace: 0,
      } : undefined,
    });
    const { margin, innerWidth, innerHeight } = layout;
    const yLanes = computeAxisLanes({
      axisSide: 'left',
      labels: categories,
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
      telemetryMeta: { chartId: 'horizontal-bar', viewportWidth: width, viewportHeight: height },
    });

    const values = data.map(d => d.value);
    const [xMin, xMax] = niceDomain(values, {
      includeZero: true,
      minOverride: config.xMin,
      maxOverride: config.xMax,
    });

    const yScale = scaleBand({ domain: data.map(d => d.name), range: [0, innerHeight], padding: 0.35 });
    const xScale = scaleLinear({ domain: [xMin, xMax], range: [0, innerWidth] });
    const xLanes = computeAxisLanes({
      axisSide: 'bottom',
      labels: xScale.ticks(5).map((v) => String(v)),
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: xTickLength,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
      telemetryMeta: { chartId: 'horizontal-bar', viewportWidth: width, viewportHeight: height },
    });
    const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
    if (axisDebugEnabled) {
      reportAxisLaneClamp(yLanes);
      reportAxisLaneClamp(xLanes);
    }

    const bw = computeBarWidth(yScale.bandwidth(), innerHeight, data.length, { maxFraction: 0.085 });
    const bOff = (yScale.bandwidth() - bw) / 2;
    const effectiveRadius = Math.min(theme.barRadius, bw * 0.15);

    const yAxisResolved = resolveYAxisLabels({
      labels: categories,
      innerHeight,
      baseFontSize: tickSize,
      fontFamily: theme.fontBody,
      truncateLabels: !!config.truncateXAxisLabels,
    });

    const rv = config.showReferenceLine && config.referenceValue ? parseFloat(config.referenceValue) : null;

    return (
      <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
        <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
          <Group left={margin.left} top={margin.top}>
            {showHLegend && hLegendItems.length > 0 && (
              <ChartLegend
                items={hLegendItems}
                innerWidth={innerWidth}
                theme={theme}
                position="top"
                transform={`translate(0, ${legendTopTranslateY(hEstimatedLegendRows, width, height)})`}
              />
            )}
            {config.showGrid && (
              <GridColumns
                scale={xScale}
                height={innerHeight}
                strokeDasharray="3 3"
                stroke={theme.gridColor}
                strokeOpacity={theme.gridAlpha}
              />
            )}

            {data.map((d, i) => {
              const y = (yScale(d.name) ?? 0) + bOff;
              const barW = xScale(d.value);
              if (barW <= 0) return null;
              return (
                <g key={d.name} onMouseMove={(e) => showTip(e, formatCompactNumber(d.value, config.valueDecimals || 1))} style={{ cursor: 'pointer' }}>
                  <path d={rightRoundedRect(0, y, barW, bw, effectiveRadius)} fill={hBarFillFn(d, i)} />
                  {config.showValues && (
                    <text x={barW + 5} y={y + bw / 2} dominantBaseline="middle" fontSize={valueLabelSize} fontWeight={500} fontFamily={fonts.number} fill={getLabelColor(baseLabelBg)}>
                      {formatCompactNumber(d.value, config.valueDecimals || 1)}
                    </text>
                  )}
                </g>
              );
            })}

            {rv !== null && (
              <VRefLine xScale={xScale} value={rv} label={config.referenceLabel} innerHeight={innerHeight} color={referenceLineColor} fontFamily={fonts.label} />
            )}

            <AxisLeft
              scale={yScale}
              tickValues={yAxisResolved.tickValues}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={yTickLength}
              tickFormat={yAxisResolved.tickFormat}
              tickLabelProps={() => ({
                ...axisTickProps(fonts.label),
                fontSize: yAxisResolved.fontSize,
                textAnchor: 'end',
                dx: -yTickGap,
                dy: yTickBaselineNudge,
              })}
            />
            <AxisBottom
              scale={xScale}
              top={innerHeight}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={xTickLength}
              tickFormat={getCompactTickFormatter(xScale.domain()[1])}
              tickLabelProps={() => ({ ...axisTickProps(), textAnchor: 'middle', dy: xTickBaselineNudgeNumeric })}
            />
            <AxisLabels
              xlabel={config.xlabel}
              ylabel={config.ylabel}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              yAxisTitleOffset={yLanes.titleOffsetPx}
              xAxisTitleY={xAxisTitleY}
              theme={theme}
            />
            {axisDebugEnabled && (
              <AxisLaneDebugOverlay
                lanes={[
                  {
                    axisSide: 'left',
                    tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                    titleOffsetPx: yLanes.titleOffsetPx,
                    clamped: yLanes.clamped,
                    spineX: 0,
                    spineY1: 0,
                    spineY2: innerHeight,
                  },
                  {
                    axisSide: 'bottom',
                    tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                    titleOffsetPx: xLanes.titleOffsetPx,
                    clamped: xLanes.clamped,
                    spineY: innerHeight,
                    spineX1: 0,
                    spineX2: innerWidth,
                  },
                ]}
              />
            )}
          </Group>
        </svg>
        <Tip />
      </div>
    );
  }

  // ─── horizontal-grouped-bar ────────────────────────────────────────────────
  if (chartType.id === 'horizontal-grouped-bar') {
    const userSeriesKeys = data._seriesKeys;
    const userLabelMap = data._labelMap;
    const numSeries = userSeriesKeys ? userSeriesKeys.length : (config.numSeries || 3);
    const seriesKeys = userSeriesKeys || Array.from({ length: numSeries }, (_, i) => `series${i + 1}`);
    const seriesColorsBase = getColors(config, numSeries, theme);
    const seriesColors = seriesKeys.map((k, i) => {
      const label = userLabelMap ? (userLabelMap[k] || k) : `Series ${i + 1}`;
      return resolveColorWithOverrides(seriesColorsBase[i], label, config.colorOverrides);
    });
    const legendItems = seriesKeys.map((k, i) => ({
      key: k,
      label: userLabelMap ? (userLabelMap[k] || k) : `Series ${i + 1}`,
      color: seriesColors[i],
      hatchIndex: config.usePatternFills ? i : null,
    }));
    const categories = data.map(d => d.name);
    const allValues = data.flatMap(d => seriesKeys.map(k => d[k] || 0));
    const [xMin, xMax] = niceDomain(allValues, {
      includeZero: true,
      minOverride: config.xMin,
      maxOverride: config.xMax,
    });
    const hTitleFontSz = computeTitleFontSize(config.title, width);
    const hLegendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
    const hEstimatedLegendRows = config.showLegend
      ? computeLegendRows(legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, hLegendMarkerScale).rowCount
      : 0;
    const hTopLegendSpace = config.showLegend ? legendTopSpace(hEstimatedLegendRows) : 0;
    const layout = computeChartLayout({
      width,
      height,
      title: config.title,
      titleFontSize: hTitleFontSz || undefined,
      xLabels: categories,
      tickFontSize: tickSize,
      tickFontFamily: fonts.label,
      hasXLabel: !!config.xlabel,
      hasYLabel: !!config.ylabel,
      orientation: 'horizontal',
      baseRight: 80,
      baseBottom: 70,
      horizontalLeftMax: 180,
      legend: {
        show: !!config.showLegend,
        position: 'top',
        topSpace: hTopLegendSpace,
        rightSpace: 0,
        bottomSpace: 0,
      },
    });
    const { margin, innerWidth, innerHeight } = layout;
    const yLanes = computeAxisLanes({
      axisSide: 'left',
      labels: categories,
      tickFontSize: tickSize,
      tickFontFamily: fonts.label,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
      telemetryMeta: { chartId: 'horizontal-grouped-bar', viewportWidth: width, viewportHeight: height },
    });
    const xTicks = scaleLinear({ domain: [xMin, xMax], range: [0, 100] }).ticks();
    const xLanes = computeAxisLanes({
      axisSide: 'bottom',
      labels: xTicks.map((v) => String(v)),
      tickFontSize: tickSize,
      tickFontFamily: fonts.number,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: xTickLength,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
      telemetryMeta: { chartId: 'horizontal-grouped-bar', viewportWidth: width, viewportHeight: height },
    });
    const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
    const yOuter = scaleBand({ domain: categories, range: [0, innerHeight], padding: 0.2 });
    const yInner = scaleBand({ domain: seriesKeys, range: [0, yOuter.bandwidth()], padding: 0.05 });
    const xScale = scaleLinear({ domain: [xMin, xMax], range: [0, innerWidth] });
    const bw = computeBarWidth(yInner.bandwidth(), innerHeight, categories.length * numSeries, { maxFraction: Math.max(0.012, 0.15 / numSeries) });
    const bOff = (yInner.bandwidth() - bw) / 2;
    const effectiveRadius = Math.min(theme.barRadius, bw * 0.15);
    const rv = config.showReferenceLine && config.referenceValue ? parseFloat(config.referenceValue) : null;

    return (
      <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
        <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
          <Group left={margin.left} top={margin.top}>
            {config.showLegend && (
              <ChartLegend items={legendItems} innerWidth={innerWidth} theme={theme} position="top" transform={`translate(0, ${legendTopTranslateY(hEstimatedLegendRows, width, height)})`} />
            )}
            {config.usePatternFills && <HatchPatternDefs idPrefix="horizontal-grouped-pattern" colors={seriesColors} />}
            {config.showGrid && (
              <GridColumns scale={xScale} height={innerHeight} strokeDasharray="3 3" stroke={theme.gridColor} strokeOpacity={theme.gridAlpha} />
            )}
            {data.map((d) => {
              const groupY = yOuter(d.name) ?? 0;
              return (
                <g key={d.name}>
                  {seriesKeys.map((key, sIdx) => {
                    const val = d[key] || 0;
                    const y = groupY + (yInner(key) ?? 0) + bOff;
                    const barW = xScale(val) - xScale(0);
                    if (barW <= 0) return null;
                    return (
                      <g key={key} onMouseMove={(e) => showTip(e, formatCompactNumber(val, config.valueDecimals || 1))} style={{ cursor: 'pointer' }}>
                        <path d={rightRoundedRect(0, y, barW, bw, effectiveRadius)} fill={seriesColors[sIdx]} />
                        {config.usePatternFills && <path d={rightRoundedRect(0, y, barW, bw, effectiveRadius)} fill={`url(#horizontal-grouped-pattern-${sIdx})`} />}
                        {config.showValues && (
                          <text x={barW + 5} y={y + bw / 2} dominantBaseline="middle" fontSize={valueLabelSize} fontWeight={500} fontFamily={fonts.number} fill={getLabelColor(baseLabelBg)}>
                            {formatCompactNumber(val, config.valueDecimals || 1)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}
            {rv !== null && <VRefLine xScale={xScale} value={rv} label={config.referenceLabel} innerHeight={innerHeight} color={referenceLineColor} fontFamily={fonts.label} />}
            <AxisLeft
              scale={yOuter}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={yTickLength}
              tickLabelProps={() => ({ ...axisTickProps(fonts.label), textAnchor: 'end', dx: -yTickGap, dy: yTickBaselineNudge })}
            />
            <AxisBottom
              scale={xScale}
              top={innerHeight}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={xTickLength}
              tickFormat={getCompactTickFormatter(xScale.domain()[1])}
              tickLabelProps={() => ({ ...axisTickProps(fonts.number), textAnchor: 'middle', dy: xTickBaselineNudgeNumeric })}
            />
            <AxisLabels xlabel={config.xlabel} ylabel={config.ylabel} innerWidth={innerWidth} innerHeight={innerHeight} yAxisTitleOffset={yLanes.titleOffsetPx} xAxisTitleY={xAxisTitleY} theme={theme} />
          </Group>
        </svg>
        <Tip />
      </div>
    );
  }

  // ─── horizontal-stacked-bar ────────────────────────────────────────────────
  if (chartType.id === 'horizontal-stacked-bar') {
    const userStackKeys = data._seriesKeys;
    const userStackLabelMap = data._labelMap;
    const numStacks = userStackKeys ? userStackKeys.length : (config.numStacks || 3);
    const stackKeys = userStackKeys || Array.from({ length: numStacks }, (_, i) => `stack${i + 1}`);
    const stackColorsBase = getColors(config, numStacks, theme).reverse();
    const stackColors = stackKeys.map((k, i) => {
      const label = userStackLabelMap ? (userStackLabelMap[k] || k) : `Stack ${i + 1}`;
      return resolveColorWithOverrides(stackColorsBase[i], label, config.colorOverrides);
    });
    const legendItems = stackKeys.map((k, i) => ({
      key: k,
      label: userStackLabelMap ? (userStackLabelMap[k] || k) : `Stack ${i + 1}`,
      color: stackColors[i],
      hatchIndex: config.usePatternFills ? i : null,
    }));
    const categories = data.map(d => d.name);
    const totals = data.map(d => stackKeys.reduce((sum, k) => sum + (d[k] || 0), 0));
    const [xMin, xMax] = niceDomain(totals, {
      includeZero: true,
      minOverride: config.xMin,
      maxOverride: config.xMax,
    });
    const hsTitleFontSz = computeTitleFontSize(config.title, width);
    const hsLegendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
    const hsEstimatedLegendRows = config.showLegend
      ? computeLegendRows(legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, hsLegendMarkerScale).rowCount
      : 0;
    const hsTopLegendSpace = config.showLegend ? legendTopSpace(hsEstimatedLegendRows) : 0;
    const layout = computeChartLayout({
      width,
      height,
      title: config.title,
      titleFontSize: hsTitleFontSz || undefined,
      xLabels: categories,
      tickFontSize: tickSize,
      tickFontFamily: fonts.label,
      hasXLabel: !!config.xlabel,
      hasYLabel: !!config.ylabel,
      orientation: 'horizontal',
      baseRight: 80,
      baseBottom: 70,
      horizontalLeftMax: 180,
      legend: {
        show: !!config.showLegend,
        position: 'top',
        topSpace: hsTopLegendSpace,
        rightSpace: 0,
        bottomSpace: 0,
      },
    });
    const { margin, innerWidth, innerHeight } = layout;
    const yLanes = computeAxisLanes({
      axisSide: 'left',
      labels: categories,
      tickFontSize: tickSize,
      tickFontFamily: fonts.label,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
      telemetryMeta: { chartId: 'horizontal-stacked-bar', viewportWidth: width, viewportHeight: height },
    });
    const xTicks = scaleLinear({ domain: [xMin, xMax], range: [0, 100] }).ticks();
    const xLanes = computeAxisLanes({
      axisSide: 'bottom',
      labels: xTicks.map((v) => String(v)),
      tickFontSize: tickSize,
      tickFontFamily: fonts.number,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: xTickLength,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
      telemetryMeta: { chartId: 'horizontal-stacked-bar', viewportWidth: width, viewportHeight: height },
    });
    const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
    const yScale = scaleBand({ domain: categories, range: [0, innerHeight], padding: 0.2 });
    const xScale = scaleLinear({ domain: [xMin, xMax], range: [0, innerWidth] });
    const bw = computeBarWidth(yScale.bandwidth(), innerHeight, categories.length, { maxFraction: 0.08 });
    const bOff = (yScale.bandwidth() - bw) / 2;
    const effectiveRadius = Math.min(theme.barRadius, bw * 0.15);
    const rv = config.showReferenceLine && config.referenceValue ? parseFloat(config.referenceValue) : null;
    const stackedData = data.map(d => {
      let cumulative = 0;
      return {
        name: d.name,
        segments: stackKeys.map((key, sIdx) => {
          const val = d[key] || 0;
          const from = cumulative;
          const to = cumulative + val;
          cumulative = to;
          return { key, val, from, to, isTop: sIdx === numStacks - 1 };
        }),
      };
    });

    return (
      <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
        <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
          <Group left={margin.left} top={margin.top}>
            {config.showLegend && (
              <ChartLegend items={legendItems} innerWidth={innerWidth} theme={theme} position="top" transform={`translate(0, ${legendTopTranslateY(hsEstimatedLegendRows, width, height)})`} />
            )}
            {config.usePatternFills && <HatchPatternDefs idPrefix="horizontal-stacked-pattern" colors={stackColors} />}
            {config.showGrid && (
              <GridColumns scale={xScale} height={innerHeight} strokeDasharray="3 3" stroke={theme.gridColor} strokeOpacity={theme.gridAlpha} />
            )}
            {stackedData.map((item) => {
              const y = (yScale(item.name) ?? 0) + bOff;
              return (
                <g key={item.name}>
                  {item.segments.map((seg, sIdx) => {
                    const segX = xScale(seg.from);
                    const segW = xScale(seg.to) - xScale(seg.from);
                    if (segW <= 0) return null;
                    const d = seg.isTop ? rightRoundedRect(segX, y, segW, bw, effectiveRadius) : `M${segX},${y} h${segW} v${bw} h${-segW}Z`;
                    return (
                      <g key={seg.key}>
                        <path d={d} fill={stackColors[sIdx]} onMouseMove={(e) => showTip(e, formatCompactNumber(seg.val, config.valueDecimals || 0))} style={{ cursor: 'pointer' }} />
                        {config.usePatternFills && <path d={d} fill={`url(#horizontal-stacked-pattern-${sIdx})`} />}
                      </g>
                    );
                  })}
                  {config.showValues && item.segments.map((seg, sIdx) => {
                    const segX = xScale(seg.from);
                    const segW = xScale(seg.to) - xScale(seg.from);
                    if (segW < 18) return null;
                    return (
                      <text key={`val-${seg.key}`} x={segX + segW / 2} y={y + bw / 2} textAnchor="middle" dominantBaseline="middle" fontSize={valueLabelSize} fontWeight={500} fontFamily={fonts.number} fill={getLabelColor(stackColors[sIdx])}>
                        {formatCompactNumber(seg.val, config.valueDecimals || 0)}
                      </text>
                    );
                  })}
                </g>
              );
            })}
            {rv !== null && <VRefLine xScale={xScale} value={rv} label={config.referenceLabel} innerHeight={innerHeight} color={referenceLineColor} fontFamily={fonts.label} />}
            <AxisLeft scale={yScale} stroke={theme.axisColor} strokeWidth={theme.spineWidth} tickStroke={theme.axisColor} tickLength={yTickLength} tickLabelProps={() => ({ ...axisTickProps(fonts.label), textAnchor: 'end', dx: -yTickGap, dy: yTickBaselineNudge })} />
            <AxisBottom scale={xScale} top={innerHeight} stroke={theme.axisColor} strokeWidth={theme.spineWidth} tickStroke={theme.axisColor} tickLength={xTickLength} tickFormat={getCompactTickFormatter(xScale.domain()[1])} tickLabelProps={() => ({ ...axisTickProps(fonts.number), textAnchor: 'middle', dy: xTickBaselineNudgeNumeric })} />
            <AxisLabels xlabel={config.xlabel} ylabel={config.ylabel} innerWidth={innerWidth} innerHeight={innerHeight} yAxisTitleOffset={yLanes.titleOffsetPx} xAxisTitleY={xAxisTitleY} theme={theme} />
          </Group>
        </svg>
        <Tip />
      </div>
    );
  }

  // ─── grouped-bar ───────────────────────────────────────────────────────────
  if (chartType.id === 'grouped-bar') {
    const userSeriesKeys = data._seriesKeys;
    const userLabelMap = data._labelMap;
    const numSeries = userSeriesKeys ? userSeriesKeys.length : (config.numSeries || 3);
    const seriesKeys = userSeriesKeys || Array.from({ length: numSeries }, (_, i) => `series${i + 1}`);
    const seriesColorsBase = getColors(config, numSeries, theme);
    const seriesColors = seriesKeys.map((k, i) => {
      const label = userLabelMap ? (userLabelMap[k] || k) : `Series ${i + 1}`;
      return resolveColorWithOverrides(seriesColorsBase[i], label, config.colorOverrides);
    });
    const legendItems = seriesKeys.map((k, i) => ({
      key: k,
      label: userLabelMap ? (userLabelMap[k] || k) : `Series ${i + 1}`,
      color: seriesColors[i],
      hatchIndex: config.usePatternFills ? i : null,
    }));

    const categories = data.map(d => d.name);
    const allValues = data.flatMap(d => seriesKeys.map(k => d[k] || 0));
    const [yMin, yMax] = niceDomain(allValues, {
      includeZero: true,
      minOverride: config.yMin,
      maxOverride: config.yMax,
    });

    const gTitleFontSz = computeTitleFontSize(config.title, width);

    // Adaptive layout (shared layout engine)
    const xAxis = resolveXAxisLabels({
      labels: categories,
      chartWidth: width,
      chartHeight: height,
      baseFontSize: tickSize,
      fontFamily: theme.fontBody,
      rotationOverride: config.labelRotation,
      truncateLabels: shouldTruncateXAxisLabels,
    });
    const yTicks = scaleLinear({ domain: [yMin, yMax], range: [100, 0] }).ticks();
    const legendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
    const estimatedLegendRows = config.showLegend
      ? computeLegendRows(legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, legendMarkerScale).rowCount
      : 0;
    const topLegendSpace = config.showLegend ? legendTopSpace(estimatedLegendRows) : 0;
    const layout = computeChartLayout({
      width,
      height,
      title: config.title,
      titleFontSize: gTitleFontSz || undefined,
      xLabels: categories,
      yTickValues: yTicks,
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      hasXLabel: !!config.xlabel,
      hasYLabel: !!config.ylabel,
      orientation: 'vertical',
      baseRight: 80,
      baseBottom: 70,
      legend: {
        show: !!config.showLegend,
        position: 'top',
        topSpace: topLegendSpace,
        rightSpace: 0,
        bottomSpace: 0,
      },
      xAxisResolved: xAxis,
    });
    const { margin, innerWidth, innerHeight } = layout;
    const yLanes = computeAxisLanes({
      axisSide: 'left',
      labels: yTicks.map((v) => String(v)),
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
      telemetryMeta: { chartId: 'grouped-bar', viewportWidth: width, viewportHeight: height },
    });
    const xLanes = computeAxisLanes({
      axisSide: 'bottom',
      labels: categories.map((c) => xAxis.tickFormat(c)),
      rotation: xAxis.rotation || 0,
      tickFontSize: xAxis.fontSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: xTickLength,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
      telemetryMeta: { chartId: 'grouped-bar', viewportWidth: width, viewportHeight: height },
    });
    if (axisDebugEnabled) {
      reportAxisLaneClamp(yLanes);
      reportAxisLaneClamp(xLanes);
    }
    const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;

    const xOuter = scaleBand({ domain: categories, range: [0, innerWidth], padding: 0.2 });
    const xInner = scaleBand({ domain: seriesKeys, range: [0, xOuter.bandwidth()], padding: 0.05 });
    const yScale = scaleLinear({ domain: [yMin, yMax], range: [innerHeight, 0] });

    const bw = computeBarWidth(xInner.bandwidth(), innerWidth, categories.length * numSeries, { maxFraction: Math.max(0.012, 0.15 / numSeries) });
    const bOff = (xInner.bandwidth() - bw) / 2;
    const effectiveRadius = Math.min(theme.barRadius, bw * 0.15);

    const rv = config.showReferenceLine && config.referenceValue ? parseFloat(config.referenceValue) : null;

    return (
      <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
        <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
          <Group left={margin.left} top={margin.top}>
            {config.showLegend && (
              <ChartLegend
                items={legendItems}
                innerWidth={innerWidth}
                theme={theme}
                position="top"
                transform={`translate(0, ${legendTopTranslateY(estimatedLegendRows, width, height)})`}
              />
            )}
            {config.usePatternFills && <HatchPatternDefs idPrefix="grouped-pattern" colors={seriesColors} />}

            {config.showGrid && (
              <GridRows
                scale={yScale}
                width={innerWidth}
                strokeDasharray="3 3"
                stroke={theme.gridColor}
                strokeOpacity={theme.gridAlpha}
              />
            )}

            {data.map((d) => {
              const groupX = xOuter(d.name) ?? 0;
              return (
                <g key={d.name}>
                  {seriesKeys.map((key, sIdx) => {
                    const val = d[key] || 0;
                    const x = groupX + (xInner(key) ?? 0) + bOff;
                    const y = yScale(val);
                    const barH = innerHeight - y;
                    if (barH <= 0) return null;
                    return (
                      <g key={key} onMouseMove={(e) => showTip(e, formatCompactNumber(val, config.valueDecimals || 1))} style={{ cursor: 'pointer' }}>
                        <path
                          d={topRoundedRect(x, y, bw, barH, effectiveRadius)}
                          fill={seriesColors[sIdx]}
                        />
                        {config.usePatternFills && (
                          <path
                            d={topRoundedRect(x, y, bw, barH, effectiveRadius)}
                            fill={`url(#grouped-pattern-${sIdx})`}
                          />
                        )}
                        {config.showValues && (
                          <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize={valueLabelSize} fontWeight={500} fontFamily={fonts.number} fill={getLabelColor(baseLabelBg)}>
                            {formatCompactNumber(val, config.valueDecimals || 1)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {rv !== null && (
              <HRefLine yScale={yScale} value={rv} label={config.referenceLabel} innerWidth={innerWidth} color={referenceLineColor} fontFamily={fonts.label} />
            )}

            <AxisLeft
              scale={yScale}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={yTickLength}
              tickFormat={getCompactTickFormatter(yScale.domain()[1])}
              tickLabelProps={() => ({ ...axisTickProps(), textAnchor: 'end', dx: -yTickGap, dy: yTickBaselineNudge })}
            />
            <AxisBottom
              scale={xOuter}
              top={innerHeight}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={xTickLength}
              tickValues={xAxis.tickValues}
              tickFormat={xAxis.tickFormat}
              tickComponent={makeTickComponent(xAxis.rotation, { fontSize: xAxis.fontSize, fill: theme.tickColor, fontFamily: fonts.label })}
              tickLabelProps={() => ({ opacity: 0 })}
            />

            <AxisLabels
              xlabel={config.xlabel}
              ylabel={config.ylabel}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              yAxisTitleOffset={yLanes.titleOffsetPx}
              xAxisTitleY={xAxisTitleY}
              theme={theme}
            />
            {axisDebugEnabled && (
              <AxisLaneDebugOverlay
                lanes={[
                  {
                    axisSide: 'left',
                    tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                    titleOffsetPx: yLanes.titleOffsetPx,
                    clamped: yLanes.clamped,
                    spineX: 0,
                    spineY1: 0,
                    spineY2: innerHeight,
                  },
                  {
                    axisSide: 'bottom',
                    tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                    titleOffsetPx: xLanes.titleOffsetPx,
                    clamped: xLanes.clamped,
                    spineY: innerHeight,
                    spineX1: 0,
                    spineX2: innerWidth,
                  },
                ]}
              />
            )}
          </Group>
        </svg>
        <Tip />
      </div>
    );
  }

  // ─── stacked-bar ───────────────────────────────────────────────────────────
  if (chartType.id === 'stacked-bar') {
    const userStackKeys = data._seriesKeys;
    const userStackLabelMap = data._labelMap;
    const numStacks = userStackKeys ? userStackKeys.length : (config.numStacks || 3);
    const stackKeys = userStackKeys || Array.from({ length: numStacks }, (_, i) => `stack${i + 1}`);
    const stackColorsBase = getColors(config, numStacks, theme).reverse();
    const stackColors = stackKeys.map((k, i) => {
      const label = userStackLabelMap ? (userStackLabelMap[k] || k) : `Stack ${i + 1}`;
      return resolveColorWithOverrides(stackColorsBase[i], label, config.colorOverrides);
    });
    const legendItems = stackKeys.map((k, i) => ({
      key: k,
      label: userStackLabelMap ? (userStackLabelMap[k] || k) : `Stack ${i + 1}`,
      color: stackColors[i],
      hatchIndex: config.usePatternFills ? i : null,
    }));

    const categories = data.map(d => d.name);
    const totals = data.map(d => stackKeys.reduce((sum, k) => sum + (d[k] || 0), 0));
    const [yMin, yMax] = niceDomain(totals, {
      includeZero: true,
      minOverride: config.yMin,
      maxOverride: config.yMax,
    });

    const sTitleFontSz = computeTitleFontSize(config.title, width);

    // Adaptive layout (shared layout engine)
    const xAxis = resolveXAxisLabels({
      labels: categories,
      chartWidth: width,
      chartHeight: height,
      baseFontSize: tickSize,
      fontFamily: theme.fontBody,
      rotationOverride: config.labelRotation,
      truncateLabels: shouldTruncateXAxisLabels,
    });
    const yTicks = scaleLinear({ domain: [yMin, yMax], range: [100, 0] }).ticks();
    const sLegendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
    const sEstimatedLegendRows = config.showLegend
      ? computeLegendRows(legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, sLegendMarkerScale).rowCount
      : 0;
    const sTopLegendSpace = config.showLegend ? legendTopSpace(sEstimatedLegendRows) : 0;
    const layout = computeChartLayout({
      width,
      height,
      title: config.title,
      titleFontSize: sTitleFontSz || undefined,
      xLabels: categories,
      yTickValues: yTicks,
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      hasXLabel: !!config.xlabel,
      hasYLabel: !!config.ylabel,
      orientation: 'vertical',
      baseRight: 80,
      baseBottom: 70,
      legend: {
        show: !!config.showLegend,
        position: 'top',
        topSpace: sTopLegendSpace,
        rightSpace: 0,
        bottomSpace: 0,
      },
      xAxisResolved: xAxis,
    });
    const { margin, innerWidth, innerHeight } = layout;
    const yLanes = computeAxisLanes({
      axisSide: 'left',
      labels: yTicks.map((v) => String(v)),
      tickFontSize: tickSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: yTickLength,
      availableMargin: margin.left,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
      telemetryMeta: { chartId: 'stacked-bar', viewportWidth: width, viewportHeight: height },
    });
    const xLanes = computeAxisLanes({
      axisSide: 'bottom',
      labels: categories.map((c) => xAxis.tickFormat(c)),
      rotation: xAxis.rotation || 0,
      tickFontSize: xAxis.fontSize,
      tickFontFamily: theme.fontBody,
      titleFontSize: labelSize,
      showTicks: true,
      tickLength: xTickLength,
      spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
      telemetryMeta: { chartId: 'stacked-bar', viewportWidth: width, viewportHeight: height },
    });
    if (axisDebugEnabled) {
      reportAxisLaneClamp(yLanes);
      reportAxisLaneClamp(xLanes);
    }
    const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;

    const xScale = scaleBand({ domain: categories, range: [0, innerWidth], padding: 0.2 });
    const yScale = scaleLinear({ domain: [yMin, yMax], range: [innerHeight, 0] });

    const bw = computeBarWidth(xScale.bandwidth(), innerWidth, categories.length, { maxFraction: 0.08 });
    const bOff = (xScale.bandwidth() - bw) / 2;
    const effectiveRadius = Math.min(theme.barRadius, bw * 0.15);

    const rv = config.showReferenceLine && config.referenceValue ? parseFloat(config.referenceValue) : null;

    // Pre-compute stacked segments for each category
    const stackedData = data.map(d => {
      let cumulative = yMin;
      return {
        name: d.name,
        segments: stackKeys.map((key, sIdx) => {
          const val = d[key] || 0;
          const from = cumulative;
          const to = cumulative + val;
          cumulative = to;
          return { key, val, from, to, isTop: sIdx === numStacks - 1 };
        }),
      };
    });

    return (
      <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
        <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
          <Group left={margin.left} top={margin.top}>
            {config.showLegend && (
              <ChartLegend
                items={legendItems}
                innerWidth={innerWidth}
                theme={theme}
                position="top"
                transform={`translate(0, ${legendTopTranslateY(sEstimatedLegendRows, width, height)})`}
              />
            )}
            {config.usePatternFills && <HatchPatternDefs idPrefix="stacked-pattern" colors={stackColors} />}

            {config.showGrid && (
              <GridRows
                scale={yScale}
                width={innerWidth}
                strokeDasharray="3 3"
                stroke={theme.gridColor}
                strokeOpacity={theme.gridAlpha}
              />
            )}

            {stackedData.map((item) => {
              const x = (xScale(item.name) ?? 0) + bOff;
              return (
                <g key={item.name}>
                  {item.segments.map((seg, sIdx) => {
                    const segY = yScale(seg.to);
                    const segH = yScale(seg.from) - yScale(seg.to);
                    if (segH <= 0) return null;
                    const d = seg.isTop
                      ? topRoundedRect(x, segY, bw, segH, effectiveRadius)
                      : `M${x},${segY} h${bw} v${segH} h${-bw}Z`;
                    return (
                      <g key={seg.key}>
                        <path
                          d={d}
                          fill={stackColors[sIdx]}
                          onMouseMove={(e) => showTip(e, formatCompactNumber(seg.val, config.valueDecimals || 0))}
                          style={{ cursor: 'pointer' }}
                        />
                        {config.usePatternFills && (
                          <path
                            d={d}
                            fill={`url(#stacked-pattern-${sIdx})`}
                            onMouseMove={(e) => showTip(e, formatCompactNumber(seg.val, config.valueDecimals || 0))}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </g>
                    );
                  })}
                  {config.showValues && item.segments.map((seg, sIdx) => {
                    const segY = yScale(seg.to);
                    const segH = yScale(seg.from) - yScale(seg.to);
                    if (segH < 14) return null;
                    return (
                      <text
                        key={`val-${seg.key}`}
                        x={x + bw / 2}
                        y={segY + segH / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={valueLabelSize}
                        fontWeight={500}
                        fontFamily={fonts.number}
                        fill={getLabelColor(config.usePatternFills ? stackColors[sIdx] : stackColors[sIdx])}
                      >
                        {formatCompactNumber(seg.val, config.valueDecimals || 0)}
                      </text>
                    );
                  })}
                </g>
              );
            })}

            {rv !== null && (
              <HRefLine yScale={yScale} value={rv} label={config.referenceLabel} innerWidth={innerWidth} color={referenceLineColor} fontFamily={fonts.label} />
            )}

            <AxisLeft
              scale={yScale}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={yTickLength}
              tickFormat={getCompactTickFormatter(yScale.domain()[1])}
              tickLabelProps={() => ({ ...axisTickProps(), textAnchor: 'end', dx: -yTickGap, dy: yTickBaselineNudge })}
            />
            <AxisBottom
              scale={xScale}
              top={innerHeight}
              stroke={theme.axisColor}
              strokeWidth={theme.spineWidth}
              tickStroke={theme.axisColor}
              tickLength={xTickLength}
              tickValues={xAxis.tickValues}
              tickFormat={xAxis.tickFormat}
              tickComponent={makeTickComponent(xAxis.rotation, { fontSize: xAxis.fontSize, fill: theme.tickColor, fontFamily: fonts.label })}
              tickLabelProps={() => ({ opacity: 0 })}
            />

            <AxisLabels
              xlabel={config.xlabel}
              ylabel={config.ylabel}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              yAxisTitleOffset={yLanes.titleOffsetPx}
              xAxisTitleY={xAxisTitleY}
              theme={theme}
            />
            {axisDebugEnabled && (
              <AxisLaneDebugOverlay
                lanes={[
                  {
                    axisSide: 'left',
                    tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                    titleOffsetPx: yLanes.titleOffsetPx,
                    clamped: yLanes.clamped,
                    spineX: 0,
                    spineY1: 0,
                    spineY2: innerHeight,
                  },
                  {
                    axisSide: 'bottom',
                    tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                    titleOffsetPx: xLanes.titleOffsetPx,
                    clamped: xLanes.clamped,
                    spineY: innerHeight,
                    spineX1: 0,
                    spineX2: innerWidth,
                  },
                ]}
              />
            )}
          </Group>
        </svg>
        <Tip />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full" style={{ color: theme.ui?.emptyStateText || theme.tickColor }}>
      Unknown bar chart type: {chartType.id}
    </div>
  );
}

export default BarChartPreview;
